
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { collectErrors, generalErrorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import {
  COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING,
  EmailUse,
  emailUseOf,
  IApiFieldError,
  identityIntegrationsService,
  IIdentityIntegration,
  IIdentityIntegrationUpdate,
} from '@/services/identityIntegrationsService';

const MAPPED_FIELDS = ['jit_provisioning_enabled'];

/**
 * The JIT toggle, plus whether the current configuration can actually produce
 * a user: a target Community and a trusted email. The email mapping itself
 * lives in the attribute mapping card — it is one canonical field among five,
 * not a JIT setting.
 */
@Component({ name: 'JitCard' })
export default class JitCard extends Vue {
  @Prop({ required: true })
  protected integration!: IIdentityIntegration;
  @Prop({ default: false })
  protected canManage!: boolean;

  protected jitEnabled: boolean = false;
  protected busy: boolean = false;
  protected errors: IApiFieldError[] = [];

  created() {
    this.syncFromIntegration();
  }

  @Watch('integration')
  onIntegrationChanged() {
    this.syncFromIntegration();
  }

  get emailUse(): EmailUse {
    return emailUseOf(this.integration);
  }

  get isAttributeMapped(): boolean {
    return this.integration.community_resolution.mode === COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING;
  }

  /**
   * Fixed mode needs exactly one Community in scope to know where to
   * provision. Attribute-mapped resolution answers that from the assertion
   * instead, so the warning does not apply to it.
   */
  get showFixedCommunityWarning(): boolean {
    return (
      this.integration.jit_provisioning_enabled &&
      !this.isAttributeMapped &&
      this.integration.community_ids.length !== 1
    );
  }

  get fixedCommunityWarning(): string {
    return this.integration.community_ids.length === 0
      ? (this.$t('identityIntegrations.jit.noCommunityWarning') as string)
      : (this.$t('identityIntegrations.jit.manyCommunitiesWarning', [this.integration.community_ids.length]) as string);
  }

  get showNoCommunityMappingWarning(): boolean {
    return (
      this.integration.jit_provisioning_enabled &&
      this.isAttributeMapped &&
      this.integration.community_resolution.mapping_count === 0
    );
  }

  get dirty(): boolean {
    return this.jitEnabled !== this.integration.jit_provisioning_enabled;
  }

  get generalError(): string | null {
    return generalErrorMessage(this, this.errors, MAPPED_FIELDS);
  }

  async submit() {
    const changes: IIdentityIntegrationUpdate = { jit_provisioning_enabled: this.jitEnabled };

    this.busy = true;
    this.errors = [];
    try {
      const updated = await identityIntegrationsService.update(this.integration.id, changes);
      this.$uiBus.emit('info', this.$t('identityIntegrations.saved') as string);
      this.$emit('updated', updated);
    } catch (error) {
      this.errors = collectErrors(error);
    } finally {
      this.busy = false;
    }
  }

  private syncFromIntegration() {
    this.jitEnabled = this.integration.jit_provisioning_enabled;
    this.errors = [];
  }
}
